import { useEffect } from "react";

/**
 * Utility functions for managing page titles with SEO best practices
 */

// Default brand name for the application
const BRAND_NAME = "Lumo Pages";

/**
 * Sets the document title following SEO best practices
 * @param {string} title - The specific page title
 * @param {string} siteName - Optional site/brand name (defaults to BRAND_NAME)
 * @param {string} separator - Separator between title and brand name (defaults to " | ")
 */
export const setPageTitle = (
  title,
  siteName = BRAND_NAME,
  separator = " | "
) => {
  if (!title) {
    document.title = siteName;
    return;
  }

  // Keep titles under 60 characters for optimal SEO
  const fullTitle = `${title}${separator}${siteName}`;
  document.title = fullTitle.length > 60 ? title : fullTitle;
};

/**
 * Sets the meta description for the page
 * @param {string} description - The meta description to set
 */
export const setMetaDescription = (description) => {
  let metaDescription = document.querySelector('meta[name="description"]');
  if (!metaDescription) {
    metaDescription = document.createElement("meta");
    metaDescription.name = "description";
    document.head.appendChild(metaDescription);
  }
  metaDescription.content = description;
};

/**
 * Hook to set page title and meta description on component mount
 * @param {string} title - The page title to set
 * @param {string} description - Optional meta description
 * @param {string} siteName - Optional site name
 */
export const usePageTitle = (title, description, siteName) => {
  useEffect(() => {
    setPageTitle(title, siteName);

    if (description) {
      setMetaDescription(description);
    }

    // Cleanup: restore default title when component unmounts
    return () => {
      setPageTitle("Live", BRAND_NAME);
    };
  }, [title, description, siteName]);
};

// Pre-defined page titles for consistency
export const PAGE_TITLES = {
  NOT_FOUND: "Page Not Found",
  LIMIT_REACHED: "Page Unavailable",
  ERROR: "Error",
  LOADING: "Loading",
  DEFAULT: "Live",
};

// Pre-defined meta descriptions for consistency
export const META_DESCRIPTIONS = {
  NOT_FOUND:
    "The page you're looking for could not be found. Please check the URL or contact the page creator.",
  LIMIT_REACHED:
    "This Lumo page is currently unavailable. Please try again later or contact the creator.",
  ERROR:
    "An error occurred while loading this page. Please refresh or try again later.",
  LOADING: "Loading your Lumo page...",
  DEFAULT: "Create and share beautiful, interactive web pages with Lumo Pages.",
};
